// Apolo IDE — E2-T02 · useSessionTrace.
//
// READ-ONLY fetch of the durable per-turn trace for a Code session. Reads
// GET /api/learning/events?session_id=<id>&limit=200 through the shared
// apiFetch (auto-Authorization), tolerates the { events: [...] } envelope, and
// folds the flat list into a per-turn timeline + totals via the PURE
// buildTrace transformer. Never writes; a failed/empty response just yields an
// empty trace so the panel degrades gracefully.

import { useCallback, useEffect, useRef, useState } from 'react';
import { apiFetch } from '../lib/api.js';
import { buildTrace } from '../lib/sessionTrace.js';

const DEFAULT_LIMIT = 200;
const EMPTY_TRACE = buildTrace([]);

/**
 * Fetch the raw session_events rows for a session. Best-effort: resolves to []
 * on a missing id, an API error, or a malformed envelope — never throws.
 *
 * @param {string} sessionId
 * @param {{ limit?: number, signal?: AbortSignal, token?: string }} [opts]
 * @returns {Promise<Array<object>>}
 */
export async function fetchSessionEvents(sessionId, { limit = DEFAULT_LIMIT, signal, token } = {}) {
  if (!sessionId) return [];
  const params = new URLSearchParams({ session_id: sessionId, limit: String(limit) });
  const res = await apiFetch(`/api/learning/events?${params.toString()}`, { signal, token });
  if (res?.ok && Array.isArray(res.data?.events)) return res.data.events;
  return [];
}

/**
 * @param {string} sessionId
 * @param {{ enabled?: boolean, limit?: number }} [opts]
 * @returns {{ trace: ReturnType<typeof buildTrace>, loading: boolean, error: string|null, refetch: () => Promise<void> }}
 */
export function useSessionTrace(sessionId, { enabled = true, limit = DEFAULT_LIMIT } = {}) {
  const [trace, setTrace] = useState(EMPTY_TRACE);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Guards against a stale response (slow fetch resolving after a newer one /
  // after unmount) clobbering current state.
  const reqIdRef = useRef(0);

  const load = useCallback(async () => {
    if (!sessionId) {
      setTrace(EMPTY_TRACE);
      return;
    }
    const myReq = ++reqIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const events = await fetchSessionEvents(sessionId, { limit });
      if (myReq !== reqIdRef.current) return; // superseded — drop
      setTrace(buildTrace(events));
    } catch (e) {
      if (myReq !== reqIdRef.current) return;
      setError(e?.message || String(e));
      setTrace(EMPTY_TRACE);
    } finally {
      if (myReq === reqIdRef.current) setLoading(false);
    }
  }, [sessionId, limit]);

  useEffect(() => {
    if (!enabled) return undefined;
    load();
    return () => { reqIdRef.current += 1; }; // invalidate in-flight on unmount/dep change
  }, [enabled, load]);

  return { trace, loading, error, refetch: load };
}
